<template>
  <div class="clearfix" v-if="!loader">
    <div class="spinner-grow text-danger" style="width: 3rem; height: 3rem;margin-top:25vh; margin-left:42%; margin-bottom:5vh" role="status">
      <span class="visually-hidden">{{ $t('merchants.loading') }}</span>
    </div>
  </div>
  <div v-if="loader" class="card mb-25 border-0 rounded-0 bg-white letter-spacing" id="back">
    <div
      class="card-head box-shadow bg-white d-lg-flex align-items-center justify-content-between p-15 p-sm-20 p-md-25 flex-wrap gap-10"
    >
      <div class="d-sm-flex flex-wrap align-items-center w-100 w-lg-30">
        <button
          class="default-btn d-block text-center w-80 border-0 box-shadow fw-medium text-white rounded-1 pt-12 pb-12 ps-25 pe-25 pt-md-11 pb-md-11 ps-md-30 pe-md-30 rounded-1 fs-md-15 fs-lg-16 d-inline-block mb-10 mb-lg-0"
          type="button"
          data-bs-toggle="modal"
          data-bs-target="#createMerchantModal"
          @click="openCreateModal"
          v-if="current_permissions('create_all_merchant_account')"
        >
          {{ $t('merchants.create') }}
          <i class="flaticon-plus position-relative ms-5 top-2 fs-15"></i>
        </button>
      </div>
      <div class="d-sm-flex align-items-center">
        <div class="search-box-area position-relative me-sm-10 mt-10 mt-sm-0">
          <input
            v-model="searchQuery"
            type="text"
            class="form-control shadow-none fw-medium text-black fs-md-15 fs-lg-16"
            :placeholder="$t('merchants.search_organization')"
            @keyup.enter="searchOrganizations"
          />
        </div>
        <button
          class="dot-btn lh-1 position-relative top-3 bg-transparent border-0 shadow-none p-0 transition d-inline-block mt-10 mt-sm-0"
          type="button"
          @click="searchOrganizations"
        >
          <i class="flaticon-search-interface-symbol"></i>
        </button>
      </div>
    </div>
    <div class="card-body p-15 p-sm-20 p-md-25">
      <div class="table-responsive">
        <table id="" class="table text-nowrap align-middle mb-0">
          <thead>
            <tr>
              <th scope="col" class="text-uppercase fw-medium shadow-none text-body-tertiary fs-13 pt-0 ps-0">
                {{ $t('merchants.organization') }}
              </th>
              <th scope="col" class="text-uppercase fw-medium shadow-none text-body-tertiary fs-13 pt-0">
                {{ $t('merchants.main_account') }}
              </th>
              <th scope="col" class="text-uppercase fw-medium shadow-none text-body-tertiary fs-13 pt-0">
                {{ $t('merchants.main_balance') }}
              </th>
              <th scope="col" class="text-uppercase fw-medium shadow-none text-body-tertiary fs-13 pt-0">
                {{ $t('merchants.other_accounts') }}
              </th>
              <th scope="col" class="text-uppercase fw-medium shadow-none text-body-tertiary fs-13 pt-0">
                {{ $t('merchants.status') }}
              </th>
              <th scope="col" class="text-uppercase fw-medium shadow-none text-body-tertiary fs-13 pt-0 text-end pe-0"></th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(org) in organizationsList" :key="org.code">
              <th class="shadow-none lh-1 fw-medium text-black-emphasis title ps-0">
                <div class="d-flex align-items-center">
                  <div class="d-flex align-items-center fs-md-15 fs-lg-16">
                    {{ org.name }}
                  </div>
                </div>
              </th>

              <td class="shadow-none lh-1 fw-medium text-black-emphasis">
                <span v-if="merchantsLoading[org.code]" class="spinner-border spinner-border-sm text-secondary" role="status"></span>
                <span v-else-if="getMainAccount(org.code)">{{ getMainAccount(org.code)?.name }}</span>
                <span v-else class="text-body-tertiary">—</span>
              </td>

              <td class="shadow-none lh-1 fw-medium">
                <span
                    v-if="!merchantsLoading[org.code] && getMainAccount(org.code)"
                    class="fs-6 fw-bold text-black"
                >
                    {{ formatAmount(getMainAccount(org.code)?.ledgerAccountBalance?.balance) }}
                </span>

                <span
                    v-else-if="!merchantsLoading[org.code]"
                    class="text-body-tertiary"
                >
                    —
                </span>
             </td>

              <td class="shadow-none lh-1 fw-medium text-black-emphasis">
                <button
                  v-if="!merchantsLoading[org.code] && getOtherAccounts(org.code).length"
                  type="button"
                  class="dot-btn lh-1 position-relative bg-transparent border-0 shadow-none p-0 transition d-inline-flex align-items-center"
                  @click="handleShowOtherAccounts(org)"
                  :title="$t('merchants.view_other_accounts', { count: getOtherAccounts(org.code).length })"
                >
                  <i class="flaticon-view fs-16 me-5"></i>
                  <span class="badge text-outline-primary">{{ getOtherAccounts(org.code).length }}</span>
                </button>
                <span v-else-if="!merchantsLoading[org.code]" class="text-body-tertiary">—</span>
              </td>

              <td class="shadow-none lh-1 fw-medium">
                <span v-if="!merchantsLoading[org.code] && getMainAccount(org.code)">
                  <span class="badge text-outline-success" v-if="getMainAccount(org.code)?.status==='ACTIVE'">{{ $t('merchants.active') }}</span>
                  <span class="badge text-outline-danger" v-else-if="getMainAccount(org.code)?.status==='INACTIVE'">{{ $t('merchants.inactive') }}</span>
                  <span class="badge text-outline-info" v-else>{{ getMainAccount(org.code)?.status }}</span>
                </span>
              </td>

              <td class="shadow-none lh-1 fw-medium text-body-tertiary text-end pe-0">
                <button
                  class="dropdown-toggle lh-1 bg-transparent border-0 shadow-none p-0 transition"
                  type="button"
                  @click="toggleActionMenu($event, org.code)"
                >
                  <i class="flaticon-dots"></i>
                </button>
              </td>
            </tr>
            <tr v-if="!organizationsList.length">
              <td colspan="6" class="text-center text-body-tertiary py-25">
                {{ $t('merchants.no_organization') }}
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div v-if="orgTotal>0" class="pagination-area d-md-flex mt-15 mt-md-25 mb-0 justify-content-between align-items-center">
        <p class="mb-0 text-paragraph">
          {{ $t('merchants.showing', { count: organizationsList.length }) }} <span class="fw-bold">{{ orgTotal }}</span>
        </p>
        <nav class="mt-15 mt-md-0">
          <ul class="pagination mb-0">
            <li v-if="(orgPage-1)>0" class="page-item">
              <a class="page-link" @click="fetchOrganizations(orgPage-1, searchQuery)" aria-label="Previous">
                <i class="flaticon-chevron-1"></i>
              </a>
            </li>
            <li v-for="(numPage) in orgNumPages" :key="numPage" class="page-item">
              <a v-if="numPage==orgPage" class="page-link active" href="#">{{ numPage}}</a>
              <a v-else class="page-link" @click="fetchOrganizations(numPage, searchQuery)">{{ numPage}}</a>
            </li>
            <li v-if="(orgPage+1)<=orgNumPages" class="page-item">
              <a class="page-link" @click="fetchOrganizations(orgPage+1, searchQuery)" aria-label="Next">
                <i class="flaticon-chevron"></i>
              </a>
            </li>
          </ul>
        </nav>
      </div>
    </div>
  </div>

  <Teleport to="body">
    <ul
      v-if="openMenuId"
      class="dropdown-menu show action-menu-fixed"
      :style="{ top: menuPosition.top + 'px', left: menuPosition.left + 'px' }"
      @click.stop
    >
      <li v-if="current_permissions('list_ledger_accounts')">
        <a @click="handleView(openMenuId)" href="javascript:void(0);" class="dropdown-item d-flex align-items-center">
          <i class="flaticon-view lh-1 me-8 position-relative top-1"></i>
          {{ $t('merchants.view') }}
        </a>
      </li>
      <li v-if="current_permissions('update_ledger_account')">
        <a @click="handleEdit(openMenuId)" href="javascript:void(0);" class="dropdown-item d-flex align-items-center">
          <i class="flaticon-pen lh-1 me-8 position-relative top-1"></i>
          {{ $t('merchants.edit') }}
        </a>
      </li>
      <li v-if="current_permissions('merchant_account_status')">
        <a @click="handleChangeStatus(openMenuId)" href="javascript:void(0);" class="dropdown-item d-flex align-items-center">
          <i class="flaticon-pen lh-1 me-8 position-relative top-1"></i>
          {{ $t('merchants.change_status') }}
        </a>
      </li>
    </ul>
  </Teleport>

  <!-- Modal Créer un compte marchand -->
  <div class="modal fade" id="createMerchantModal" tabindex="-1" aria-hidden="true">
    <div class="modal-dialog modal-dialog-centered">
      <div class="modal-content create-merchant-modal-content">
        <form @submit.prevent="submitCreateMerchant">
          <div class="modal-header">
            <h1 class="modal-title fs-5">{{ $t('merchants.create_merchant_accounts') }}</h1>
            <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
          </div>
          <div class="modal-body">
            <div v-if="createError!==''" class="mb-15">
              <h6 class="text-red fw-bold mb-0 text-center" style="color: red;">{{ createError }}</h6>
            </div>

            <label class="d-block text-black fw-semibold mb-10">{{ $t('merchants.organization') }}</label>

            <div class="create-org-search position-relative mb-15">
              <input
                v-model="createOrgSearch"
                type="text"
                class="form-control shadow-none fw-medium text-black fs-md-15 fs-lg-16 ps-40"
                :placeholder="$t('merchants.search_organization')"
                @keyup.enter="searchCreateOrganizations"
              />
              <i class="flaticon-search-interface-symbol create-org-search-icon"></i>
            </div>

            <div class="create-org-list">
              <div
                v-for="org in createOrgList"
                :key="org.code"
                class="create-org-block"
                :class="{ active: selectedCreateOrganization === org.code }"
                @click="selectedCreateOrganization = org.code"
              >
                <div class="create-org-block-radio">
                  <span class="create-org-block-radio-dot"></span>
                </div>
                <div class="create-org-block-info">
                  <span class="create-org-block-name">{{ org.name }}</span>
                  <span class="create-org-block-sub" v-if="org.email">{{ org.email }}</span>
                </div>
              </div>

              <div v-if="!createOrgLoading && !createOrgList.length" class="text-center text-body-tertiary py-20">
                {{ $t('merchants.no_organization') }}
              </div>

              <div v-if="createOrgLoading" class="text-center py-20">
                <span class="spinner-border spinner-border-sm text-secondary" role="status"></span>
              </div>

              <div v-if="!createOrgLoading && createOrgPagesLeft>0" class="text-center pt-10">
                <button type="button" class="btn btn-link p-0 fs-13" @click="loadMoreCreateOrganizations">
                  {{ $t('merchants.load_more_organizations') }}
                </button>
              </div>
            </div>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-outline-secondary" data-bs-dismiss="modal">{{ $t('merchants.cancel') }}</button>
            <button type="submit" :disabled="!btnLoadCreate || !selectedCreateOrganization" class="btn text-white" style="background-color: #EB9355;">
              <span v-if="!btnLoadCreate" class="spinner-grow spinner-grow-sm" role="status" aria-hidden="true" style="color: #FFF;"></span>
              <span style="color: #FFF;">{{ $t('merchants.create') }}</span>
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>

  <!-- Modal Autres comptes de l'organisation -->
  <div class="modal fade" id="otherAccountsModal" tabindex="-1" aria-hidden="true">
    <div class="modal-dialog modal-lg modal-dialog-centered modal-fullscreen-sm-down">
      <div class="modal-content">
        <div class="modal-header">
          <h1 class="modal-title fs-5">{{ $t('merchants.other_accounts_title') }} — {{ currentOrgForOthers?.name }}</h1>
          <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
        </div>
        <div class="modal-body">
          <div class="table-responsive">
            <table class="table text-nowrap align-middle mb-0">
              <thead>
                <tr>
                  <th class="text-uppercase fw-medium shadow-none text-body-tertiary fs-13 pt-0 ps-0">{{ $t('merchants.type') }}</th>
                  <th class="text-uppercase fw-medium shadow-none text-body-tertiary fs-13 pt-0">{{ $t('merchants.balance') }}</th>
                  <th class="text-uppercase fw-medium shadow-none text-body-tertiary fs-13 pt-0">{{ $t('merchants.blocked_balance') }}</th>
                  <th class="text-uppercase fw-medium shadow-none text-body-tertiary fs-13 pt-0">{{ $t('merchants.status') }}</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="acc in currentOrgOtherAccounts" :key="acc.code">
                  <td class="fw-medium"><span class="badge text-outline-primary">{{ acc.merchantAccountType }}</span></td>
                  <td class="fw-medium">{{ formatAmount(acc.ledgerAccountBalance?.balance) }}</td>
                  <td class="fw-medium">{{ formatAmount(acc.ledgerAccountBalance?.blockedBalance) }}</td>
                  <td class="fw-medium">
                    <span class="badge text-outline-success" v-if="acc.status==='ACTIVE'">{{ $t('merchants.active') }}</span>
                    <span class="badge text-outline-danger" v-else-if="acc.status==='INACTIVE'">{{ $t('merchants.inactive') }}</span>
                    <span class="badge text-outline-info" v-else>{{ acc.status }}</span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
        <div class="modal-footer">
          <button type="button" class="btn btn-outline-secondary" data-bs-dismiss="modal">{{ $t('merchants.close') }}</button>
        </div>
      </div>
    </div>
  </div>

  <!-- Modal Voir : infos organisation + tous les comptes -->
  <div class="modal fade" id="viewOrgModal" tabindex="-1" aria-hidden="true">
    <div class="modal-dialog modal-lg modal-dialog-centered modal-fullscreen-sm-down">
      <div class="modal-content">
        <div class="modal-header">
          <h1 class="modal-title fs-5">{{ currentViewOrg?.name }}</h1>
          <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
        </div>
        <div class="modal-body">
          <h6 class="fw-bold mb-15">{{ $t('merchants.organization_info') }}</h6>
          <div class="row g-3 mb-25">
            <div class="col-12 col-md-4" v-if="hasValue(currentViewOrg?.code)">
              <label class="d-block text-black fw-semibold mb-10">{{ $t('merchants.code') }}</label>
              <input :value="currentViewOrg?.code" type="text" class="form-control shadow-none fw-medium text-black fs-md-15 fs-lg-16" readonly />
            </div>
            <div class="col-12 col-md-4" v-if="hasValue(currentViewOrg?.description)">
              <label class="d-block text-black fw-semibold mb-10">{{ $t('merchants.description') }}</label>
              <input :value="currentViewOrg?.description" type="text" class="form-control shadow-none fw-medium text-black fs-md-15 fs-lg-16" readonly />
            </div>
            <div class="col-12 col-md-4" v-if="hasValue(currentViewOrg?.status)">
              <label class="d-block text-black fw-semibold mb-10">{{ $t('merchants.organization_status') }}</label>
              <input :value="currentViewOrg?.status" type="text" class="form-control shadow-none fw-medium text-black fs-md-15 fs-lg-16" readonly />
            </div>
            <div class="col-12 col-md-4" v-if="hasValue(currentViewOrg?.email)">
              <label class="d-block text-black fw-semibold mb-10">{{ $t('merchants.email') }}</label>
              <input :value="currentViewOrg?.email" type="text" class="form-control shadow-none fw-medium text-black fs-md-15 fs-lg-16" readonly />
            </div>
            <div class="col-12 col-md-4" v-if="hasValue(currentViewOrg?.contact)">
              <label class="d-block text-black fw-semibold mb-10">{{ $t('merchants.contact') }}</label>
              <input :value="currentViewOrg?.contact" type="text" class="form-control shadow-none fw-medium text-black fs-md-15 fs-lg-16" readonly />
            </div>
            <div class="col-12 col-md-4" v-if="hasValue(currentViewOrg?.type)">
              <label class="d-block text-black fw-semibold mb-10">{{ $t('merchants.type') }}</label>
              <input :value="currentViewOrg?.type" type="text" class="form-control shadow-none fw-medium text-black fs-md-15 fs-lg-16" readonly />
            </div>
            <div class="col-12 col-md-4" v-if="hasValue(currentViewOrg?.bankDomiciliation)">
              <label class="d-block text-black fw-semibold mb-10">{{ $t('merchants.bank_domiciliation') }}</label>
              <input :value="currentViewOrg?.bankDomiciliation" type="text" class="form-control shadow-none fw-medium text-black fs-md-15 fs-lg-16" readonly />
            </div>
            <div class="col-12 col-md-4" v-if="hasValue(currentViewOrg?.rib)">
              <label class="d-block text-black fw-semibold mb-10">{{ $t('merchants.rib') }}</label>
              <input :value="currentViewOrg?.rib" type="text" class="form-control shadow-none fw-medium text-black fs-md-15 fs-lg-16" readonly />
            </div>
            <div class="col-12 col-md-4" v-if="hasValue(currentViewOrg?.createdAt)">
              <label class="d-block text-black fw-semibold mb-10">{{ $t('merchants.created_on') }}</label>
              <input :value="formatDateTimeFr(currentViewOrg?.createdAt)" type="text" class="form-control shadow-none fw-medium text-black fs-md-15 fs-lg-16" readonly />
            </div>
          </div>

          <h6 class="fw-bold mb-15">{{ $t('merchants.organization_accounts') }}</h6>
          <div class="table-responsive">
            <table class="table text-nowrap align-middle mb-0">
              <thead>
                <tr>
                  <th class="text-uppercase fw-medium shadow-none text-body-tertiary fs-13 pt-0 ps-0">{{ $t('merchants.type') }}</th>
                  <th class="text-uppercase fw-medium shadow-none text-body-tertiary fs-13 pt-0">{{ $t('merchants.balance') }}</th>
                  <th class="text-uppercase fw-medium shadow-none text-body-tertiary fs-13 pt-0">{{ $t('merchants.blocked_balance') }}</th>
                  <th class="text-uppercase fw-medium shadow-none text-body-tertiary fs-13 pt-0">{{ $t('merchants.track_balance') }}</th>
                  <th class="text-uppercase fw-medium shadow-none text-body-tertiary fs-13 pt-0">{{ $t('merchants.status') }}</th>
                  <th class="text-uppercase fw-medium shadow-none text-body-tertiary fs-13 pt-0">{{ $t('merchants.created_at') }}</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="acc in currentViewAccounts" :key="acc.code">
                  <td class="fw-medium"><span class="badge text-outline-primary">{{ acc.merchantAccountType }}</span></td>
                  <td class="fw-medium">{{ formatAmount(acc.ledgerAccountBalance?.balance) }}</td>
                  <td class="fw-medium">{{ formatAmount(acc.ledgerAccountBalance?.blockedBalance) }}</td>
                  <td class="fw-medium">{{ acc.trackBalance ? $t('merchants.yes') : $t('merchants.no') }}</td>
                  <td class="fw-medium">
                    <span class="badge text-outline-success" v-if="acc.status==='ACTIVE'">{{ $t('merchants.active') }}</span>
                    <span class="badge text-outline-danger" v-else-if="acc.status==='INACTIVE'">{{ $t('merchants.inactive') }}</span>
                    <span class="badge text-outline-info" v-else>{{ acc.status }}</span>
                  </td>
                  <td class="fw-medium">{{ formatDateTimeFr(acc.createdAt) }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
        <div class="modal-footer">
          <button type="button" class="btn btn-outline-secondary" data-bs-dismiss="modal">{{ $t('merchants.close') }}</button>
        </div>
      </div>
    </div>
  </div>

  <!-- Modal Modifier : choix du compte puis formulaire -->
  <div class="modal fade" id="editMerchantModal" tabindex="-1" aria-hidden="true">
    <div class="modal-dialog modal-lg modal-dialog-centered modal-fullscreen-sm-down">
      <div class="modal-content">
        <form @submit.prevent="submitEditMerchant">
          <div class="modal-header">
            <h1 class="modal-title fs-5">{{ $t('merchants.edit_account') }} — {{ currentOrgForEdit?.name }}</h1>
            <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
          </div>
          <div class="modal-body">
            <div v-if="editError!==''" class="mb-15">
              <h6 class="text-red fw-bold mb-0 text-center" style="color: red;">{{ editError }}</h6>
            </div>

            <div class="form-group mb-20">
              <label class="d-block text-black fw-semibold mb-10">{{ $t('merchants.account_to_edit') }}</label>
              <select v-model="selectedEditAccountCode" class="form-select fs-md-15 text-black shadow-none" @change="onSelectEditAccount" required>
                <option value="">{{ $t('merchants.select_account') }}</option>
                <option v-for="acc in editAccounts" :value="acc.code" :key="acc.code">
                  {{ acc.merchantAccountType }}
                </option>
              </select>
            </div>

            <div class="row g-3" v-if="selectedEditAccountCode">
              <div class="col-12 col-md-6">
                <label class="d-block text-black fw-semibold mb-10">{{ $t('merchants.name') }}</label>
                <input v-model="editForm.name" type="text" class="form-control shadow-none fw-medium text-black fs-md-15 fs-lg-16" required />
              </div>
              <div class="col-12 col-md-6">
                <label class="d-block text-black fw-semibold mb-10">{{ $t('merchants.description') }}</label>
                <input v-model="editForm.description" type="text" class="form-control shadow-none fw-medium text-black fs-md-15 fs-lg-16" />
              </div>

              <div class="col-12 col-md-4">
                <label class="d-block text-black fw-semibold mb-10">{{ $t('merchants.account_type') }}</label>
                <select v-model="editForm.merchantAccountType" class="form-select fs-md-15 text-black shadow-none" required>
                  <option value="MAIN">MAIN</option>
                  <option value="COLLECT">COLLECT</option>
                  <option value="AVAILABLE">AVAILABLE</option>
                  <option value="PAYOUT">PAYOUT</option>
                </select>
              </div>
              <div class="col-12 col-md-4">
                <label class="d-block text-black fw-semibold mb-10">{{ $t('merchants.status') }}</label>
                <select v-model="editForm.status" class="form-select fs-md-15 text-black shadow-none" required>
                  <option value="ACTIVE">ACTIVE</option>
                  <option value="INACTIVE">INACTIVE</option>
                </select>
              </div>
              <div class="col-12 col-md-4 d-flex align-items-center" style="padding-top: 32px;">
                <div class="form-check">
                  <input v-model="editForm.trackBalance" class="form-check-input" type="checkbox" id="trackBalanceCheck" />
                  <label class="form-check-label fw-semibold" for="trackBalanceCheck">
                    {{ $t('merchants.track_balance') }}
                  </label>
                </div>
              </div>
            </div>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-outline-secondary" data-bs-dismiss="modal">{{ $t('merchants.cancel') }}</button>
            <button type="submit" :disabled="!btnLoadEdit || !selectedEditAccountCode" class="btn text-white" style="background-color: #EB9355;">
              <span v-if="!btnLoadEdit" class="spinner-grow spinner-grow-sm" role="status" aria-hidden="true" style="color: #FFF;"></span>
              <span style="color: #FFF;">{{ $t('merchants.save') }}</span>
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>

  <!-- Modal Changer le statut : choix du compte puis statut -->
  <div class="modal fade" id="changeMerchantStatusModal" tabindex="-1" aria-hidden="true">
    <div class="modal-dialog modal-dialog-centered">
      <div class="modal-content edit-status-modal-content">
        <button type="button" class="btn-close status-modal-close" data-bs-dismiss="modal" aria-label="Close"></button>

        <div class="edit-status-header">
          <div class="edit-status-icon">
            <i class="flaticon-pen"></i>
          </div>
          <h5 class="mb-0 mt-10">{{ $t('merchants.change_status_title') }}</h5>
          <p class="text-muted fs-13 mb-0 mt-5">{{ currentOrgForStatus?.name }}</p>
        </div>

        <div class="modal-body pt-10">
          <div class="form-group mb-15">
            <label class="d-block text-black fw-semibold mb-10">{{ $t('merchants.account') }}</label>
            <select v-model="selectedStatusAccountCode" class="form-select fs-md-15 text-black shadow-none edit-status-select" @change="onSelectStatusAccount">
              <option value="">{{ $t('merchants.select_account') }}</option>
              <option v-for="acc in statusAccounts" :value="acc.code" :key="acc.code">
                {{ acc.merchantAccountType }}
              </option>
            </select>
          </div>

          <div class="form-group mb-15" v-if="selectedStatusAccountCode">
            <label class="d-block text-black fw-semibold mb-10">{{ $t('merchants.status') }}</label>
            <select v-model="newStatusValue" class="form-select fs-md-15 text-black shadow-none edit-status-select">
              <option value="ACTIVE">ACTIVE</option>
              <option value="INACTIVE">INACTIVE</option>
            </select>
          </div>

          <p v-if="statusError" class="text-danger fs-13 mt-10 mb-0">{{ statusError }}</p>
        </div>

        <!-- ✅ CORRECTION : le footer n'apparaît qu'après sélection du compte -->
        <div class="modal-footer border-0 pt-0" v-if="selectedStatusAccountCode">
          <button type="button" class="btn btn-outline-secondary" data-bs-dismiss="modal">{{ $t('merchants.cancel') }}</button>
          <button type="button" :disabled="!btnLoadStatus" class="btn edit-status-submit-btn" @click="submitChangeStatus()">
            <span v-if="!btnLoadStatus" class="spinner-grow spinner-grow-sm" role="status" aria-hidden="true" style="color: #FFF;"></span>
            <span style="color: #FFF;">{{ $t('merchants.validate') }}</span>
          </button>
        </div>
      </div>
    </div>
  </div>

  <div id="snackbar"></div>
</template>

<script setup lang="ts">
import { ref, reactive, onMounted, onBeforeUnmount, inject, nextTick } from "vue";
import { useI18n } from "vue-i18n";
import axios from 'axios';
import { Modal } from 'bootstrap';

import router from '../../../router/index';
import { current_permissions } from '../../../utils/permission';
import { showToast, checkSessionValidate } from "@/utils/function";

const { t } = useI18n();

const backApi = inject('back');
const token = localStorage.getItem('login_token');

declare interface Organization {
  code: string;
  name: string;
  description?: string;
  contact?: string;
  status?: string;
  email?: string;
  bankDomiciliation?: string;
  rib?: string;
  cryptoAccount?: string;
  type?: string;
  createdAt?: string;
  updatedAt?: string | null;
}

declare interface Merchant {
  code: string;
  name: string;
  description: string;
  status: string;
  type: string;
  merchantAccountType: string;
  providerBalanceType: string | null;
  trackBalance: boolean;
  user: any;
  organization: { code: string; name: string } | null;
  provider: any;
  operator: any;
  ledgerAccountBalance: { code: string; balance: number; blockedBalance: string } | null;
  createdAt: string;
  updatedAt: string | null;
}

const merchantsUrl = backApi + '/secure/merchants/extended';
const organizationsUrl = backApi + '/secure/organizations';

const loader = ref(false);

// ═══ Liste des organisations (ligne = organisation) ═══
const organizationsList = ref(new Array<Organization>());
const orgPage = ref(1);
const orgTotal = ref(0);
const orgNumPages = ref(1);
const searchQuery = ref('');

// Comptes marchands par organisation (cache)
const merchantsByOrg = reactive<Record<string, Merchant[]>>({});
const merchantsLoading = reactive<Record<string, boolean>>({});

function getAllAccounts(code: string): Merchant[] {
  return merchantsByOrg[code] || [];
}
function getMainAccount(code: string): Merchant | undefined {
  return getAllAccounts(code).find((m) => m.merchantAccountType === 'MAIN');
}
function getOtherAccounts(code: string): Merchant[] {
  return getAllAccounts(code).filter((m) => m.merchantAccountType !== 'MAIN');
}

const hasValue = (value: any): boolean => {
  if (value === null || value === undefined) return false;
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (trimmed === '' || trimmed === 'null' || trimmed === 'undefined') return false;
  }
  return true;
};

// ✅ Formatage des montants avec espace comme séparateur de milliers
function formatAmount(value: any): string {
  const num = Number(value || 0);
  if (isNaN(num)) return '0';
  // toLocaleString('fr-FR') utilise des espaces insécables ; on les remplace par des espaces normales
  return num.toLocaleString('fr-FR').replace(/\u202f/g, ' ').replace(/\u00a0/g, ' ');
}

function formatDateTimeFr(value: string | null | undefined) {
  if (!value) return '';
  const d = new Date(value);
  if (isNaN(d.getTime())) return '';
  const day = String(d.getDate()).padStart(2, '0');
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const year = d.getFullYear();
  const hours = String(d.getHours()).padStart(2, '0');
  const minutes = String(d.getMinutes()).padStart(2, '0');
  const seconds = String(d.getSeconds()).padStart(2, '0');
  return `${day}/${month}/${year} ${hours}:${minutes}:${seconds}`;
}

function handleAxiosError(error: any, fallback = t('merchants.error_occurred')) {
  if (error.response) {
    if (error.response.status === 401) {
      router.push('/');
    } else {
      showToast(error.response.data?.message ?? fallback, "red");
    }
  }
}

async function fetchMerchantsForOrg(code: string) {
  merchantsLoading[code] = true;
  try {
    const response = await axios.get(`${merchantsUrl}?organization=${code}`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    merchantsByOrg[code] = response.data.data ?? response.data ?? [];
  } catch (error) {
    merchantsByOrg[code] = [];
  } finally {
    merchantsLoading[code] = false;
  }
}

async function fetchOrganizations(pageArg = 1, search = '') {
  loader.value = false;
  try {
    const searchParam = search ? `?organization=${encodeURIComponent(search)}` : '';
    const response = await axios.get(`${organizationsUrl}/${pageArg}${searchParam}`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    organizationsList.value = response.data.organizations || [];
    orgPage.value = pageArg;
    orgTotal.value = parseInt(response.data.pagination?.total ?? organizationsList.value.length);
    orgNumPages.value = parseInt(response.data.pagination?.numPages ?? 1);
    loader.value = true;

    await Promise.all(organizationsList.value.map((org) => fetchMerchantsForOrg(org.code)));
  } catch (error) {
    loader.value = true;
    handleAxiosError(error, t('merchants.error_loading_organizations'));
  }
}

function searchOrganizations() {
  fetchOrganizations(1, searchQuery.value);
}

// ═══ Organisations pour le modal Créer (liste indépendante, paginée à la demande) ═══
const createOrgList = ref(new Array<Organization>());
const createOrgLoading = ref(false);
const createOrgPage = ref(1);
const createOrgPagesLeft = ref(0);
const createOrgSearch = ref('');

async function fetchCreateOrganizations(pageArg = 1, append = false, search = '') {
  createOrgLoading.value = true;
  try {
    const searchParam = search ? `?organization=${encodeURIComponent(search)}` : '';
    const response = await axios.get(`${organizationsUrl}/${pageArg}${searchParam}`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    const rawList: Organization[] = response.data.organizations || [];
    createOrgPage.value = pageArg;
    createOrgPagesLeft.value = response.data.pagination?.pagesLeft ?? 0;

    // On exclut les organisations qui ont déjà des comptes marchands
    const checks = await Promise.all(rawList.map(async (org) => {
      try {
        const r = await axios.get(`${merchantsUrl}?organization=${org.code}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const accounts = r.data.data ?? r.data ?? [];
        return { org, hasAccounts: accounts.length > 0 };
      } catch {
        return { org, hasAccounts: false };
      }
    }));
    const withoutAccounts = checks.filter((c) => !c.hasAccounts).map((c) => c.org);

    createOrgList.value = append ? createOrgList.value.concat(withoutAccounts) : withoutAccounts;
  } catch (error) {
    handleAxiosError(error, t('merchants.error_loading_organizations'));
  } finally {
    createOrgLoading.value = false;
  }
}

function loadMoreCreateOrganizations() {
  fetchCreateOrganizations(createOrgPage.value + 1, true, createOrgSearch.value);
}

function searchCreateOrganizations() {
  fetchCreateOrganizations(1, false, createOrgSearch.value);
}

const selectedCreateOrganization = ref('');
const btnLoadCreate = ref(true);
const createError = ref('');

function openCreateModal() {
  createError.value = '';
  selectedCreateOrganization.value = '';
  createOrgSearch.value = '';
  fetchCreateOrganizations(1, false, '');
}

function submitCreateMerchant() {
  if (!selectedCreateOrganization.value) {
    createError.value = t('merchants.please_select_organization');
    return;
  }
  createError.value = '';
  btnLoadCreate.value = false;

  axios.post(`${backApi}/secure/merchants/all`, {
    organization: selectedCreateOrganization.value,
  }, {
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    }
  }).then(() => {
    btnLoadCreate.value = true;
    showToast(t('merchants.merchant_accounts_created'), "green");
    const modalEl = document.getElementById('createMerchantModal');
    if (modalEl) Modal.getInstance(modalEl)?.hide();
    fetchOrganizations(orgPage.value, searchQuery.value);
  }).catch((error) => {
    btnLoadCreate.value = true;
    if (error.response) {
      if (error.response.status === 401) {
        router.push('/');
      } else {
        createError.value = error.response.data?.message ?? t('merchants.error_occurred');
      }
    }
  });
}

// ═══ Modal Autres comptes ═══
const currentOrgForOthers = ref<Organization | null>(null);
const currentOrgOtherAccounts = ref(new Array<Merchant>());

function handleShowOtherAccounts(org: Organization) {
  currentOrgForOthers.value = org;
  currentOrgOtherAccounts.value = getOtherAccounts(org.code);
  const modalEl = document.getElementById('otherAccountsModal');
  if (modalEl) Modal.getOrCreateInstance(modalEl).show();
}

// ═══ Modal Voir : organisation + tous les comptes ═══
const currentViewOrg = ref<Organization | null>(null);
const currentViewAccounts = ref(new Array<Merchant>());

function handleView(orgCode: string) {
  const org = organizationsList.value.find((o) => o.code === orgCode);
  if (!org) {
    closeActionMenu();
    return;
  }
  currentViewOrg.value = org;
  currentViewAccounts.value = getAllAccounts(org.code);
  closeActionMenu();
  const modalEl = document.getElementById('viewOrgModal');
  if (modalEl) Modal.getOrCreateInstance(modalEl).show();
}

// ═══ Modal Modifier : choix du compte puis formulaire ═══
const currentOrgForEdit = ref<Organization | null>(null);
const editAccounts = ref(new Array<Merchant>());
const selectedEditAccountCode = ref('');
const editForm = ref({
  name: '',
  description: '',
  status: 'ACTIVE',
  type: 'merchant_account',
  merchantAccountType: 'MAIN',
});
const btnLoadEdit = ref(true);
const editError = ref('');

function handleEdit(orgCode: string) {
  const org = organizationsList.value.find((o) => o.code === orgCode);
  if (!org) {
    closeActionMenu();
    return;
  }
  currentOrgForEdit.value = org;
  editAccounts.value = getAllAccounts(org.code);
  selectedEditAccountCode.value = '';
  editError.value = '';
  closeActionMenu();
  const modalEl = document.getElementById('editMerchantModal');
  if (modalEl) Modal.getOrCreateInstance(modalEl).show();
}

function onSelectEditAccount() {
  const acc = editAccounts.value.find((a) => a.code === selectedEditAccountCode.value);
  if (!acc) return;
  editForm.value = {
    name: acc.name ?? '',
    description: acc.description ?? '',
    status: acc.status ?? 'ACTIVE',
    type: acc.type ?? 'merchant_account',
    merchantAccountType: acc.merchantAccountType ?? 'MAIN',
  };
}

function submitEditMerchant() {
  if (!selectedEditAccountCode.value || !currentOrgForEdit.value) return;
  editError.value = '';
  btnLoadEdit.value = false;

  const payload = {
    name: editForm.value.name,
    description: editForm.value.description,
    status: editForm.value.status,
    type: editForm.value.type,
    merchantAccountType: editForm.value.merchantAccountType,
    providerBalanceType: null,
    trackBalance: (editAccounts.value.find(a => a.code === selectedEditAccountCode.value))?.trackBalance ?? true,
    user: null,
    organization: currentOrgForEdit.value.code,
    provider: null,
    operator: null,
  };

  axios.put(`${backApi}/secure/merchants/${selectedEditAccountCode.value}/edit`, payload, {
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    }
  }).then(() => {
    btnLoadEdit.value = true;
    showToast(t('merchants.merchant_account_updated'), "green");
    const modalEl = document.getElementById('editMerchantModal');
    if (modalEl) Modal.getInstance(modalEl)?.hide();
    if (currentOrgForEdit.value) fetchMerchantsForOrg(currentOrgForEdit.value.code);
  }).catch((error) => {
    btnLoadEdit.value = true;
    if (error.response) {
      if (error.response.status === 401) {
        router.push('/');
      } else {
        editError.value = error.response.data?.message ?? t('merchants.error_occurred');
      }
    }
  });
}

// ═══ Modal Changer le statut : choix du compte puis statut ═══
const currentOrgForStatus = ref<Organization | null>(null);
const statusAccounts = ref(new Array<Merchant>());
const selectedStatusAccountCode = ref('');
const newStatusValue = ref('ACTIVE');
const btnLoadStatus = ref(true);
const statusError = ref('');

function handleChangeStatus(orgCode: string) {
  const org = organizationsList.value.find((o) => o.code === orgCode);
  if (!org) {
    closeActionMenu();
    return;
  }
  currentOrgForStatus.value = org;
  statusAccounts.value = getAllAccounts(org.code);
  selectedStatusAccountCode.value = '';
  newStatusValue.value = 'ACTIVE';
  statusError.value = '';
  closeActionMenu();
  const modalEl = document.getElementById('changeMerchantStatusModal');
  if (modalEl) Modal.getOrCreateInstance(modalEl).show();
}

function onSelectStatusAccount() {
  const acc = statusAccounts.value.find((a) => a.code === selectedStatusAccountCode.value);
  if (acc) newStatusValue.value = acc.status ?? 'ACTIVE';
}

function submitChangeStatus() {
  statusError.value = '';

  if (!selectedStatusAccountCode.value) {
    statusError.value = t('merchants.please_select_account');
    return;
  }

  btnLoadStatus.value = false;

  axios.put(`${backApi}/secure/merchants/status/${selectedStatusAccountCode.value}`, {
    status: newStatusValue.value,
  }, {
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    }
  }).then(() => {
    btnLoadStatus.value = true;
    showToast(t('merchants.status_updated'), "green");
    const modalEl = document.getElementById('changeMerchantStatusModal');
    if (modalEl) Modal.getInstance(modalEl)?.hide();
    if (currentOrgForStatus.value) fetchMerchantsForOrg(currentOrgForStatus.value.code);
  }).catch((error) => {
    btnLoadStatus.value = true;
    if (error.response) {
      if (error.response.status === 401) {
        router.push('/');
      } else {
        statusError.value = error.response.data?.message ?? t('merchants.error_occurred');
        showToast(statusError.value, "red");
      }
    }
  });
}

// ═══ Menu d'actions flottant ═══
const openMenuId = ref<string | null>(null);
const menuPosition = ref({ top: 0, left: 0 });
let menuOpenedAt = 0;

function toggleActionMenu(event: MouseEvent, code: string) {
  if (openMenuId.value === code) {
    closeActionMenu();
    return;
  }

  const button = event.currentTarget as HTMLElement;
  const rect = button.getBoundingClientRect();
  const menuWidth = 200;

  let left = rect.right - menuWidth;
  if (left < 8) left = 8;
  if (left + menuWidth > window.innerWidth - 8) {
    left = window.innerWidth - menuWidth - 8;
  }

  menuPosition.value = { top: rect.bottom + 4, left };
  openMenuId.value = code;
  menuOpenedAt = Date.now();

  nextTick(() => {
    const menuEl = document.querySelector('.action-menu-fixed') as HTMLElement | null;
    if (!menuEl) return;
    const menuHeight = menuEl.offsetHeight;

    let top = rect.bottom + 4;
    if (top + menuHeight > window.innerHeight - 8) {
      top = rect.top - menuHeight - 4;
    }
    if (top < 8) top = 8;

    menuPosition.value = { ...menuPosition.value, top };
  });
}

function closeActionMenu() {
  openMenuId.value = null;
}

function handleGlobalClick(e: MouseEvent) {
  if (!openMenuId.value) return;
  const target = e.target as HTMLElement;
  if (target.closest('.action-menu-fixed') || target.closest('.dropdown-toggle')) {
    return;
  }
  closeActionMenu();
}

function handleGlobalScroll() {
  if (!openMenuId.value) return;
  if (Date.now() - menuOpenedAt < 300) return;
  closeActionMenu();
}

onMounted(() => {
  checkSessionValidate(1);
  fetchOrganizations();

  document.addEventListener('click', handleGlobalClick);
  document.addEventListener('scroll', handleGlobalScroll, true);
});

onBeforeUnmount(() => {
  document.removeEventListener('click', handleGlobalClick);
  document.removeEventListener('scroll', handleGlobalScroll, true);
});
</script>

<style scoped>
.search-box-area {
  min-width: 220px;
}
.search-box-area input {
  height: 42px;
}

.create-merchant-modal-content .modal-body {
  padding-bottom: 10px;
}

.create-org-search input {
  height: 44px;
  padding-left: 40px;
  border-radius: 8px;
}
.create-org-search-icon {
  position: absolute;
  left: 14px;
  top: 50%;
  transform: translateY(-50%);
  color: #a3a3a3;
  font-size: 14px;
  pointer-events: none;
}

.create-org-list {
  max-height: 320px;
  overflow-y: auto;
  border: 1px solid #eee;
  border-radius: 10px;
  padding: 8px;
}

.create-org-block {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 14px;
  border: 1px solid #eee;
  border-radius: 8px;
  cursor: pointer;
  transition: border-color .15s ease, background-color .15s ease;
}
.create-org-block + .create-org-block {
  margin-top: 8px;
}
.create-org-block:hover {
  border-color: #EB9355;
  background-color: #fff9f5;
}
.create-org-block.active {
  border-color: #EB9355;
  background-color: #fff4ec;
}

.create-org-block-radio {
  flex-shrink: 0;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  border: 2px solid #ccc;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: border-color .15s ease;
}
.create-org-block.active .create-org-block-radio {
  border-color: #EB9355;
}
.create-org-block-radio-dot {
  width: 9px;
  height: 9px;
  border-radius: 50%;
  background-color: #EB9355;
  opacity: 0;
  transition: opacity .15s ease;
}
.create-org-block.active .create-org-block-radio-dot {
  opacity: 1;
}

.create-org-block-info {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}
.create-org-block-name {
  font-weight: 600;
  color: #222;
  font-size: 14.5px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.create-org-block-sub {
  font-size: 12px;
  color: #999;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.edit-status-modal-content {
  border-radius: 18px;
  border: none;
  overflow: hidden;
  position: relative;
}
.edit-status-header {
  text-align: center;
  padding: 30px 25px 15px 25px;
  background: linear-gradient(135deg, #fff6ee, #ffffff);
}
.edit-status-icon {
  width: 58px;
  height: 58px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto;
  background: linear-gradient(135deg, #f7b733, #EB9355);
  color: #fff;
  font-size: 22px;
  box-shadow: 0 8px 18px rgba(235, 147, 85, .35);
}
.edit-status-header h5 {
  font-weight: 700;
  color: #222;
}
.edit-status-select {
  border-radius: 10px;
}
.edit-status-submit-btn {
  background-color: #EB9355;
  border-radius: 10px;
  padding: 8px 22px;
}
.edit-status-submit-btn:hover {
  background-color: #3d7e24;
  border-color: #f5b800;
  color: #fff;
}
.status-modal-close {
  position: absolute;
  right: 15px;
  top: 15px;
  z-index: 5;
}

#changeMerchantStatusModal .modal-dialog {
  max-width: 400px;
  width: 90%;
}
#changeMerchantStatusModal .modal-content {
  border-radius: 12px;
}

#viewOrgModal .modal-body,
#editMerchantModal .modal-body,
#otherAccountsModal .modal-body {
  max-height: 75vh;
  overflow-y: auto;
}

@media (max-width: 767px) {
  .table-responsive table {
    font-size: 13px;
  }
  .card-head button {
    width: 100%;
    justify-content: center;
  }
  .pagination-area {
    flex-direction: column;
    gap: 10px;
  }
}

@media (min-width: 992px) {
  .w-lg-30 {
    width: 30% !important;
  }
}
</style>

<style>
.action-menu-fixed {
  position: fixed;
  z-index: 2000;
  min-width: 200px;
  max-width: calc(100vw - 20px);
  max-height: calc(100vh - 20px);
  overflow-y: auto;
  display: block;
  pointer-events: auto;
  background-color: #fff;
  border: 1px solid rgba(0,0,0,.15);
  border-radius: 6px;
  box-shadow: 0 4px 12px rgba(0,0,0,.15);
  padding: 6px 0;
}
</style>